import { useState, useMemo } from 'react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';

export function EmiCalculator() {
  const [principal, setPrincipal] = useState(2_500_000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);

  const result = useMemo(() => {
    const r = rate / 12 / 100;
    const n = years * 12;
    const emi =
      r === 0 ? principal / n : (principal * r * (1 + r) ** n) / ((1 + r) ** n - 1);
    const total = emi * n;
    const interest = total - principal;
    return { emi, total, interest, n };
  }, [principal, rate, years]);

  const fmtINR = (v: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);

  return (
    <div>
      <ToolHeader
        toolId="emi-calculator"
        title="EMI calculator"
        description="Loan EMI with total interest payable. Useful for home, auto, or personal loans."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="space-y-5">
          <Field label="Principal (₹)" hint={fmtINR(principal)}>
            <Input
              type="number"
              value={principal}
              onChange={(e) => setPrincipal(Math.max(0, Number(e.target.value) || 0))}
              className="font-mono"
            />
            <Slider
              value={principal}
              onChange={setPrincipal}
              min={50_000}
              max={50_000_000}
              step={50_000}
              showValue={false}
              className="mt-3"
            />
          </Field>
          <Field label="Interest rate" hint={`${rate.toFixed(2)}% p.a.`}>
            <Slider value={rate} onChange={setRate} min={1} max={20} step={0.05} showValue={false} />
          </Field>
          <Field label="Tenure" hint={`${years} years · ${result.n} months`}>
            <Slider value={years} onChange={setYears} min={1} max={30} showValue={false} />
          </Field>
        </Card>

        <Card className="flex flex-col">
          <div className="text-2xs uppercase tracking-wider text-fg-subtle">Monthly EMI</div>
          <div className="text-4xl font-semibold mt-2 font-mono tabular-nums">
            {fmtINR(result.emi)}
          </div>

          <div className="grid grid-cols-3 gap-2 mt-6">
            <Stat label="Principal" value={fmtINR(principal)} />
            <Stat label="Interest" value={fmtINR(result.interest)} highlight />
            <Stat label="Total" value={fmtINR(result.total)} />
          </div>

          {/* Visual */}
          <div className="mt-6">
            <div className="text-xs text-fg-muted mb-2">Composition</div>
            <div className="flex h-3 rounded-full overflow-hidden bg-bg-inset">
              <div
                className="bg-accent"
                style={{ width: `${(principal / result.total) * 100}%` }}
              />
              <div
                className="bg-warning"
                style={{ width: `${(result.interest / result.total) * 100}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-2xs text-fg-subtle mt-2">
              <span className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-accent" />
                Principal {((principal / result.total) * 100).toFixed(1)}%
              </span>
              <span className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-warning" />
                Interest {((result.interest / result.total) * 100).toFixed(1)}%
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`p-3 rounded-lg border ${highlight ? 'bg-warning/10 border-warning/20 text-warning' : 'bg-bg-inset border-border'}`}>
      <div className="text-2xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-sm font-mono font-semibold mt-1 tabular-nums">{value}</div>
    </div>
  );
}

export default EmiCalculator;
